/**
 * Section 1 of the Cluster View: the verdict banner and one index card per cluster with the full
 * criteria breakdown and the live capacity what-if controls.
 *
 * ⚠️ TWO THINGS LEFT THIS SECTION on Sep 29 2026 (Theresa).
 *
 * The **intern pool strip** across the top is deleted. It was the second intern control on the tab
 * once Balance the load arrived below it, and two controls that look alike and feed different
 * arithmetic are worse than one — this one drove the check-in index's own pool, the one below
 * drives the prioritisation state's. What it OWNED and nothing else did is `internCount` /
 * `internsOut`, which now stay at whatever the sheet seeded: the per-cluster allocation inside each
 * card still moves, but the size of the pool is the sheet's until the check-in data moves off it.
 * Reset survives as a text action on the header, because the per-cluster what-ifs still need a way
 * back and deleting the strip would have taken it with them.
 *
 * The **scoring explanation** moved to a collapsed section at the bottom of the tab
 * (`IndexExplainer`) — it is reference material that was sitting between the cards and the work.
 */
import { useMemo } from 'react';
import type { CapacityState, CheckInData } from '../../../types/taCheckin';
import {
  computeIndexes,
  indexRag,
  interviewLoadSource,
  openRolesFor,
  type ClusterIndex,
} from '../../../utils/taCheckinIndex';
import { FLAG_COLOR, RAG_COLOR, round1 } from './checkinUi';

export function ClusterIndexSection({
  data,
  state,
  onStateChange,
  onReset,
}: {
  data: CheckInData;
  state: CapacityState;
  onStateChange: (next: CapacityState) => void;
  onReset: () => void;
}) {
  const cfg = data.config;
  const list = useMemo(() => computeIndexes(data, state), [data, state]);
  const amber = cfg.index_rag?.amber ?? 100;
  const red = cfg.index_rag?.red ?? 115;
  const barMax = Math.max(red * 1.3, ...list.map((l) => l.index || 0)) || 150;

  if (!list.length) return null;
  const worst = list[0];
  const worstRag = indexRag(cfg, worst.index);
  const above = worst.index == null ? null : Math.round(worst.index - 100);

  return (
    <div className="space-y-4">
      <div
        className="flex flex-col gap-1 rounded-lg border-l-4 bg-white p-4 shadow-sm"
        style={{ borderLeftColor: RAG_COLOR[worstRag] }}
      >
        <span className="text-base font-semibold text-gray-900">
          Start with {worst.cluster} — index {worst.index == null ? '—' : Math.round(worst.index)}
          {above != null && above > 0 ? `, ${above}% above the cluster average` : ''}
          {worst.driver ? `, driven by ${worst.driver.label.toLowerCase()}` : ''}
        </span>
        <span className="flex flex-wrap items-baseline gap-x-2 text-xs text-gray-500">
          <span>
            {list.map((l) => `${l.cluster} ${l.index == null ? '—' : Math.round(l.index)}`).join(' · ')}{' '}
            · 100 = average across clusters
          </span>
          {/*
            The only survivor of the deleted intern strip. The per-cluster what-ifs inside the cards
            are still scratchpad state and still need a way back to what the sheet says.
          */}
          <button
            type="button"
            onClick={onReset}
            className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
          >
            Reset the what-ifs to the sheet
          </button>
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {list.map((c, i) => (
          <IndexCard
            key={c.cluster}
            data={data}
            entry={c}
            rank={i}
            barMax={barMax}
            amber={amber}
            red={red}
            state={state}
            onStateChange={onStateChange}
          />
        ))}
      </div>

    </div>
  );
}

function IndexCard({
  data,
  entry: c,
  rank,
  barMax,
  amber,
  red,
  state,
  onStateChange,
}: {
  data: CheckInData;
  entry: ClusterIndex;
  rank: number;
  barMax: number;
  amber: number;
  red: number;
  state: CapacityState;
  onStateChange: (next: CapacityState) => void;
}) {
  const cfg = data.config;
  const rag = indexRag(cfg, c.index);
  const live = interviewLoadSource(data) === 'live';
  const upcoming = data.interviewLoad?.byCluster?.[c.cluster]?.next7d;
  const ivStats = data.interviewLoad?.byCluster?.[c.cluster];
  const allLast = ivStats?.allLast7d;
  const panelExtra = allLast != null && ivStats ? Math.max(allLast - ivStats.last7d, 0) : null;
  const interviewHint = live
    ? `Interviews a TA person sat in over the last 7 days, counted live from Lever data in the warehouse — hiring-manager and panel-only interviews are excluded, because this is divided by TA capacity.` +
      (panelExtra ? ` A further ${panelExtra} ran on these postings without TA in the room.` : '') +
      ` ${upcoming ?? 0} TA interviews are already booked for the coming 7 days.`
    : 'From the sheet — written weekly by the interview-load agent. The warehouse count is unavailable.';

  const stats = data.pipeline.byCluster?.[c.cluster];
  const thin = stats?.thin ?? 0;
  const thinList = (stats?.thinTitles ?? []).map((t) => `${t.title} (${t.active})`).join(', ');
  const volumeHint =
    `Median active candidates per open posting. This one is scored upside down: FEWER candidates means MORE pressure, because thin pipelines are the ones needing active sourcing.` +
    (thin
      ? ` ${thin} posting${thin === 1 ? '' : 's'} under ${data.pipeline.thinThreshold ?? 8} candidates — ${thinList}.`
      : '');
  const capNow = c.cap.now;
  const capTxt =
    capNow == null
      ? 'capacity unknown'
      : `${round1(capNow)} FTE` +
        (c.cap.base != null && Math.abs(capNow - c.cap.base) > 0.005 ? ` (sheet ${round1(c.cap.base)})` : '');
  const maxR = cfg.max_out?.recruiters ?? 3;
  const rOut = state.recruitersOut?.[c.cluster] || 0;

  const rankLabel = rank === 0 ? 'highest index' : rag === 'green' ? 'below average' : rag;
  const rankStyle =
    rag === 'red'
      ? 'bg-rose-100 text-rose-700'
      : rag === 'amber'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-emerald-100 text-emerald-700';

  return (
    <div
      className="rounded-lg border border-gray-200 border-t-4 bg-white p-4 shadow-sm"
      style={{ borderTopColor: RAG_COLOR[rag] }}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900">{c.cluster}</div>
          <div className="text-xs text-gray-500">
            {openRolesFor(data, c.cluster)} open roles · {capTxt}
          </div>
          <span className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${rankStyle}`}>
            {rankLabel}
          </span>
        </div>
        <div className="text-right">
          <div className="text-3xl font-semibold text-gray-900">
            {c.index == null ? '—' : Math.round(c.index)}
          </div>
          <div className="text-[10px] text-gray-400">vs 100 avg</div>
        </div>
      </div>

      <div className="relative mt-2 h-1.5 overflow-hidden rounded bg-gray-100">
        <div
          className="absolute inset-y-0 left-0 rounded"
          style={{
            width: `${c.index == null ? 0 : Math.min((c.index / barMax) * 100, 100)}%`,
            background: RAG_COLOR[rag],
          }}
        />
        <div className="absolute inset-y-0 w-px bg-gray-400" style={{ left: `${Math.min((amber / barMax) * 100, 100)}%` }} />
        <div className="absolute inset-y-0 w-px bg-gray-400" style={{ left: `${Math.min((red / barMax) * 100, 100)}%` }} />
      </div>

      <table className="mt-3 w-full text-xs">
        <tbody>
          {c.rows.map((r) => {
            const shown =
              r.value == null ? '—' : r.fmt === 'pct' ? `${Math.round(r.value)}%` : `${round1(r.value)}${r.unit}`;
            return (
              <tr key={r.id} className="border-t border-gray-100">
                <td className="w-4 py-1">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ background: FLAG_COLOR[r.flag] }}
                    title={r.score == null ? 'no data' : `${Math.round(r.score)}% of the cluster average`}
                  />
                </td>
                <td className="py-1 text-gray-600">
                  {r.label}
                  {r.id === 'interview_load' && (
                    <span className="ml-1 text-[10px] text-gray-400" title={interviewHint}>
                      {live ? `(TA-run · +${upcoming ?? 0} booked)` : '(weekly sheet)'}
                    </span>
                  )}
                  {r.id === 'pipeline_volume' && (
                    // Inverted: low volume scores HIGH. Say so on the row
                    // itself, because a low number next to a red dot reads as
                    // a mistake otherwise.
                    <span className="ml-1 text-[10px] text-gray-400" title={volumeHint}>
                      {thin ? `(${thin} thin ↓)` : '(low = more sourcing)'}
                    </span>
                  )}
                </td>
                <td className="py-1 text-right font-medium text-gray-900">{shown}</td>
                <td className="w-9 py-1 text-right text-gray-500">
                  {r.score == null ? <span className="text-gray-300">n/a</span> : Math.round(r.score)}
                </td>
                <td className="w-14 py-1 pl-2">
                  <span className="block h-1 overflow-hidden rounded bg-gray-100">
                    <i
                      className="block h-full"
                      style={{
                        width: `${r.score == null ? 0 : Math.min((r.score / barMax) * 100, 100)}%`,
                        background: FLAG_COLOR[r.flag],
                      }}
                    />
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-2 border-t border-gray-100 pt-2 text-[11px] text-gray-500">
        {c.driver ? (
          <>
            Biggest contributor: <b className="text-gray-700">{c.driver.label}</b> —{' '}
            {Math.round(c.driver.score as number)}% of average at {c.driver.weight}% weight
          </>
        ) : (
          'Nothing above the cluster average'
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2 text-[11px] text-gray-500">
        <label className="flex items-center gap-1">
          Interns
          <input
            type="number"
            min={0}
            max={20}
            step={0.1}
            value={state.alloc[c.cluster] ?? 0}
            onChange={(e) =>
              onStateChange({
                ...state,
                alloc: { ...state.alloc, [c.cluster]: Math.max(0, Number(e.target.value) || 0) },
              })
            }
            className="w-16 rounded-md border border-gray-300 px-1.5 py-0.5 text-xs text-gray-900"
          />
        </label>
        <label className="flex items-center gap-1">
          Recruiters out
          <select
            value={rOut}
            onChange={(e) =>
              onStateChange({
                ...state,
                recruitersOut: { ...state.recruitersOut, [c.cluster]: Number(e.target.value) || 0 },
              })
            }
            className={`rounded-md border border-gray-300 px-1.5 py-0.5 text-xs ${rOut > 0 ? 'bg-rose-50 text-rose-700' : 'text-gray-900'}`}
          >
            {Array.from({ length: maxR + 1 }, (_, n) => (
              <option key={n} value={n}>
                {n === 0 ? 'none' : `−${n} FTE`}
              </option>
            ))}
          </select>
        </label>
        {c.cap.recruiterNow != null && (
          <span className={c.cap.lost > 0 ? 'font-medium text-rose-700' : ''}>
            {round1(c.cap.recruiterNow)} rec + {round1(c.cap.internEffective)} int
          </span>
        )}
      </div>
      {c.cap.scaled && (
        <div className="mt-1 text-[11px] text-rose-700">
          Intern pool is short — {round1(c.cap.internRequested)} FTE requested,{' '}
          {round1(c.cap.internEffective)} FTE actually available
        </div>
      )}
    </div>
  );
}
